import React, { useState, useMemo } from 'react';
import { AircraftStats, TechlogEntry } from '../types';
import { calculateAircraftStats, getAllAircraftRegistrations } from '../utils/calculations';
import { loadData } from '../utils/storage';

interface AircraftStatusDashboardProps {
  refreshTrigger: number;
}

type TimePeriod = 'weekly' | 'monthly' | 'yearly';

interface TrendData {
  date: string;
  oat: number[];
  alt: number[];
  ias: number[];
  tas: number[];
  torque: number[];
  propRpm: number[];
  itt: number[];
  ng: number[];
  oilPress: number[];
  oilTemp: number[];
  fuelFlow: number[];
}

const AircraftStatusDashboard: React.FC<AircraftStatusDashboardProps> = ({ refreshTrigger }) => {
  const [aircraftStats, setAircraftStats] = React.useState<AircraftStats[]>([]);
  const [techlogEntries, setTechlogEntries] = useState<TechlogEntry[]>([]);
  const [selectedAircraft, setSelectedAircraft] = useState<string | null>(null);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('weekly');
  const [showTrends, setShowTrends] = useState(false);

  React.useEffect(() => {
    const fetchData = async () => {
      const data = await loadData();
      const registrations = getAllAircraftRegistrations(data.techlogEntries);
      const stats = registrations.map(registration => 
        calculateAircraftStats(registration, data.techlogEntries)
      );
      setAircraftStats(stats);
      setTechlogEntries(data.techlogEntries);
    };
    fetchData();
  }, [refreshTrigger]);

  const getDateRangeForPeriod = (period: TimePeriod): Date => {
    const now = new Date();
    switch (period) {
      case 'weekly':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      case 'monthly':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      case 'yearly':
        return new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
    }
  };

  const calculateTrendData = useMemo(() => {
    if (!selectedAircraft) return null;

    const startDate = getDateRangeForPeriod(timePeriod);
    const filteredEntries = techlogEntries.filter(
      entry => 
        entry.aircraftRegistration === selectedAircraft &&
        new Date(entry.date) >= startDate
    );

    const trendMap = new Map<string, TrendData>();

    filteredEntries.forEach(entry => {
      const dateKey = entry.date;
      if (!trendMap.has(dateKey)) {
        trendMap.set(dateKey, {
          date: dateKey,
          oat: [],
          alt: [],
          ias: [],
          tas: [],
          torque: [],
          propRpm: [],
          itt: [],
          ng: [],
          oilPress: [],
          oilTemp: [],
          fuelFlow: [],
        });
      }

      const data = trendMap.get(dateKey)!;
      // Collect trend data from techlog entry level (cruise parameters)
      if (entry.oat !== undefined && entry.oat !== null) data.oat.push(entry.oat);
      if (entry.alt !== undefined && entry.alt !== null) data.alt.push(entry.alt);
      if (entry.ias !== undefined && entry.ias !== null) data.ias.push(entry.ias);
      if (entry.tas !== undefined && entry.tas !== null) data.tas.push(entry.tas);
      if (entry.torque !== undefined && entry.torque !== null) data.torque.push(entry.torque);
      if (entry.propRpm !== undefined && entry.propRpm !== null) data.propRpm.push(entry.propRpm);
      if (entry.itt !== undefined && entry.itt !== null) data.itt.push(entry.itt);
      if (entry.ng !== undefined && entry.ng !== null) data.ng.push(entry.ng);
      if (entry.oilPress !== undefined && entry.oilPress !== null) data.oilPress.push(entry.oilPress);
      if (entry.oilTemp !== undefined && entry.oilTemp !== null) data.oilTemp.push(entry.oilTemp);
      if (entry.fuelFlow !== undefined && entry.fuelFlow !== null) data.fuelFlow.push(entry.fuelFlow);
    });

    return Array.from(trendMap.values()).sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [selectedAircraft, timePeriod, techlogEntries]);

  const calculateAverage = (values: number[]): number => {
    if (values.length === 0) return 0;
    return values.reduce((sum, val) => sum + val, 0) / values.length;
  };

  const calculateMin = (values: number[]): number => {
    if (values.length === 0) return 0;
    return Math.min(...values);
  };

  const calculateMax = (values: number[]): number => {
    if (values.length === 0) return 0;
    return Math.max(...values);
  };

  const getTrendStats = (field: keyof Omit<TrendData, 'date'>) => {
    if (!calculateTrendData) return { avg: 0, min: 0, max: 0, count: 0 };
    
    const allValues = calculateTrendData.flatMap(d => d[field]);
    return {
      avg: calculateAverage(allValues),
      min: calculateMin(allValues),
      max: calculateMax(allValues),
      count: allValues.length,
    };
  };

  const handleViewTrends = (registration: string) => {
    setSelectedAircraft(registration);
    setShowTrends(true);
  };

  const handleCloseTrends = () => {
    setShowTrends(false);
    setSelectedAircraft(null);
  };

  const formatTime = (hours: number): string => {
    return `${hours.toFixed(1)}h`;
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (aircraftStats.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Aircraft Status Dashboard</h2>
        <div className="text-center text-gray-500 py-8">
          <p>No aircraft data available. Add some techlog entries to see aircraft statistics.</p>
        </div>
      </div>
    );
  }

  if (showTrends && selectedAircraft) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            Trend Analysis - {selectedAircraft}
          </h2>
          <button
            onClick={handleCloseTrends}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            ← Back to Dashboard
          </button>
        </div>

        {/* Time Period Selector */}
        <div className="mb-6">
          <div className="flex space-x-4">
            <button
              onClick={() => setTimePeriod('weekly')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                timePeriod === 'weekly'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Weekly (7 Days)
            </button>
            <button
              onClick={() => setTimePeriod('monthly')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                timePeriod === 'monthly'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Monthly (30 Days)
            </button>
            <button
              onClick={() => setTimePeriod('yearly')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                timePeriod === 'yearly'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Yearly (365 Days)
            </button>
          </div>
        </div>

        {/* Trend Statistics Grid */}
        {calculateTrendData && calculateTrendData.length > 0 ? (
          <div className="space-y-6">
            {/* Temperature Parameters */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Temperature Parameters</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getTrendStats('oat').count > 0 && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-blue-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">OAT (°C)</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Average:</span>
                        <span className="font-semibold">{getTrendStats('oat').avg.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Min:</span>
                        <span className="font-semibold">{getTrendStats('oat').min.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Max:</span>
                        <span className="font-semibold">{getTrendStats('oat').max.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 pt-1 border-t">
                        <span>Data Points:</span>
                        <span>{getTrendStats('oat').count}</span>
                      </div>
                    </div>
                  </div>
                )}

                {getTrendStats('itt').count > 0 && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-orange-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Cruise ITT (°C)</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Average:</span>
                        <span className="font-semibold">{getTrendStats('itt').avg.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Min:</span>
                        <span className="font-semibold">{getTrendStats('itt').min.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Max:</span>
                        <span className="font-semibold">{getTrendStats('itt').max.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 pt-1 border-t">
                        <span>Data Points:</span>
                        <span>{getTrendStats('itt').count}</span>
                      </div>
                    </div>
                  </div>
                )}

                {getTrendStats('oilTemp').count > 0 && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-red-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Oil Temp (°C)</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Average:</span>
                        <span className="font-semibold">{getTrendStats('oilTemp').avg.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Min:</span>
                        <span className="font-semibold">{getTrendStats('oilTemp').min.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Max:</span>
                        <span className="font-semibold">{getTrendStats('oilTemp').max.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 pt-1 border-t">
                        <span>Data Points:</span>
                        <span>{getTrendStats('oilTemp').count}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Pressure & Power Parameters */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Pressure & Power Parameters</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getTrendStats('oilPress').count > 0 && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-green-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Oil Pressure (PSI)</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Average:</span>
                        <span className="font-semibold">{getTrendStats('oilPress').avg.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Min:</span>
                        <span className="font-semibold">{getTrendStats('oilPress').min.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Max:</span>
                        <span className="font-semibold">{getTrendStats('oilPress').max.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 pt-1 border-t">
                        <span>Data Points:</span>
                        <span>{getTrendStats('oilPress').count}</span>
                      </div>
                    </div>
                  </div>
                )}

                {getTrendStats('torque').count > 0 && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-purple-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Torque (FT.LBS)</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Average:</span>
                        <span className="font-semibold">{getTrendStats('torque').avg.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Min:</span>
                        <span className="font-semibold">{getTrendStats('torque').min.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Max:</span>
                        <span className="font-semibold">{getTrendStats('torque').max.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 pt-1 border-t">
                        <span>Data Points:</span>
                        <span>{getTrendStats('torque').count}</span>
                      </div>
                    </div>
                  </div>
                )}

                {getTrendStats('ng').count > 0 && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-indigo-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">NG (%)</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Average:</span>
                        <span className="font-semibold">{getTrendStats('ng').avg.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Min:</span>
                        <span className="font-semibold">{getTrendStats('ng').min.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Max:</span>
                        <span className="font-semibold">{getTrendStats('ng').max.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 pt-1 border-t">
                        <span>Data Points:</span>
                        <span>{getTrendStats('ng').count}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Speed & Altitude Parameters */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Speed & Altitude Parameters</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {getTrendStats('alt').count > 0 && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-cyan-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Altitude (FT)</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Average:</span>
                        <span className="font-semibold">{getTrendStats('alt').avg.toFixed(0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Min:</span>
                        <span className="font-semibold">{getTrendStats('alt').min.toFixed(0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Max:</span>
                        <span className="font-semibold">{getTrendStats('alt').max.toFixed(0)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 pt-1 border-t">
                        <span>Data Points:</span>
                        <span>{getTrendStats('alt').count}</span>
                      </div>
                    </div>
                  </div>
                )}

                {getTrendStats('ias').count > 0 && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-teal-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">IAS (knots)</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Average:</span>
                        <span className="font-semibold">{getTrendStats('ias').avg.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Min:</span>
                        <span className="font-semibold">{getTrendStats('ias').min.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Max:</span>
                        <span className="font-semibold">{getTrendStats('ias').max.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 pt-1 border-t">
                        <span>Data Points:</span>
                        <span>{getTrendStats('ias').count}</span>
                      </div>
                    </div>
                  </div>
                )}

                {getTrendStats('tas').count > 0 && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-emerald-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">TAS (knots)</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Average:</span>
                        <span className="font-semibold">{getTrendStats('tas').avg.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Min:</span>
                        <span className="font-semibold">{getTrendStats('tas').min.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Max:</span>
                        <span className="font-semibold">{getTrendStats('tas').max.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 pt-1 border-t">
                        <span>Data Points:</span>
                        <span>{getTrendStats('tas').count}</span>
                      </div>
                    </div>
                  </div>
                )}

                {getTrendStats('propRpm').count > 0 && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-pink-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Prop RPM</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Average:</span>
                        <span className="font-semibold">{getTrendStats('propRpm').avg.toFixed(0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Min:</span>
                        <span className="font-semibold">{getTrendStats('propRpm').min.toFixed(0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Max:</span>
                        <span className="font-semibold">{getTrendStats('propRpm').max.toFixed(0)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 pt-1 border-t">
                        <span>Data Points:</span>
                        <span>{getTrendStats('propRpm').count}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Fuel Flow */}
            {getTrendStats('fuelFlow').count > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Fuel Consumption</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="border border-gray-200 rounded-lg p-4 bg-yellow-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Fuel Flow (LBS/HR)</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Average:</span>
                        <span className="font-semibold">{getTrendStats('fuelFlow').avg.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Min:</span>
                        <span className="font-semibold">{getTrendStats('fuelFlow').min.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Max:</span>
                        <span className="font-semibold">{getTrendStats('fuelFlow').max.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 pt-1 border-t">
                        <span>Data Points:</span>
                        <span>{getTrendStats('fuelFlow').count}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-700 mb-2">No Trend Data Available</h3>
            <p className="text-gray-500">
              No trend data found for {selectedAircraft} in the selected time period.
              <br />
              Add techlog entries with trend data to see analysis.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Aircraft Status Dashboard</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {aircraftStats.map((aircraft) => (
          <div
            key={aircraft.registration}
            className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-semibold text-gray-800">{aircraft.registration}</h3>
              <span className="text-xs text-gray-500">
                Updated: {formatDate(aircraft.lastUpdated)}
              </span>
            </div>

            <div className="space-y-4">
              {/* Cycles */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-blue-800">Total Cycles</h4>
                    <p className="text-2xl font-bold text-blue-900">{aircraft.totalCycles}</p>
                  </div>
                  <div className="text-blue-600">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                </div>
                <p className="text-xs text-blue-600 mt-1">Landing cycles completed</p>
              </div>

              {/* Flight Hours */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-green-800">Total Flight Hours</h4>
                    <p className="text-2xl font-bold text-green-900">{formatTime(aircraft.totalFlightHours)}</p>
                  </div>
                  <div className="text-green-600">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <p className="text-xs text-green-600 mt-1">Cumulative flight time</p>
              </div>

              {/* Utilization Metrics */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Avg Hours/Cycle:</span>
                  <span className="font-medium">
                    {aircraft.totalCycles > 0 
                      ? formatTime(aircraft.totalFlightHours / aircraft.totalCycles)
                      : '0.0h'
                    }
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Last Activity:</span>
                  <span className="font-medium">
                    {formatDate(aircraft.lastUpdated)}
                  </span>
                </div>
              </div>

              {/* View Trends Button */}
              <button
                onClick={() => handleViewTrends(aircraft.registration)}
                className="w-full mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors text-sm font-medium"
              >
                View Trend Analysis →
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Summary Statistics */}
      <div className="mt-8 p-6 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-medium text-gray-800 mb-4">Fleet Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {aircraftStats.length}
            </div>
            <div className="text-sm text-gray-600">Total Aircraft</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {aircraftStats.reduce((sum, a) => sum + a.totalCycles, 0)}
            </div>
            <div className="text-sm text-gray-600">Total Cycles</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {formatTime(aircraftStats.reduce((sum, a) => sum + a.totalFlightHours, 0))}
            </div>
            <div className="text-sm text-gray-600">Total Flight Hours</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {aircraftStats.length > 0 
                ? formatTime(
                    aircraftStats.reduce((sum, a) => sum + a.totalFlightHours, 0) / aircraftStats.length
                  )
                : '0.0h'
              }
            </div>
            <div className="text-sm text-gray-600">Avg Hours/Aircraft</div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="mt-6">
        <h3 className="text-lg font-medium text-gray-800 mb-4">Recent Activity</h3>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aircraft
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cycles
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Flight Hours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Updated
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {aircraftStats
                  .sort((a, b) => b.lastUpdated.getTime() - a.lastUpdated.getTime())
                  .slice(0, 5)
                  .map((aircraft) => (
                    <tr key={aircraft.registration} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {aircraft.registration}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{aircraft.totalCycles}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatTime(aircraft.totalFlightHours)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{formatDate(aircraft.lastUpdated)}</div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AircraftStatusDashboard;
